/**
 * Stage 1.5 — Anchor Facts.
 *
 * Sits between the forced-choice gauntlet and the test debate. Captures
 * identity facts that ride in the always-on contract (heritage, profession,
 * formative experiences) — the things a generic-thoughtful-person voice
 * cannot infer from a case base, but which shape every personal response.
 *
 * Designed to feel additive, not interrogative: by Stage 1.5 the user has
 * already done 12 minutes of real work, so asking "tell me who you are"
 * lands as context-anchoring rather than a bio gate.
 *
 * Free-form textarea, one fact per line. Skippable but encouraged — the
 * placeholder shows the kind of facts that work well so users understand
 * the shape without being interrogated by required fields.
 */

import { type FormEvent, useEffect, useRef, useState } from 'react';

interface Stage15Props {
  /** Pre-existing anchor facts (resume case). */
  initial?: string[];
  worldviewName: string;
  onContinue: (anchorFacts: string[]) => void;
  onSkip: () => void;
  onBack: () => void;
}

const PLACEHOLDER = `Jewish, secular but culturally identifying
Build drones — founder of a hardware startup
Mexican, grew up in CDMX, moved to the US at 22
Have two young kids
The illness I had at 24 still shapes how I think about time
People consistently underestimate how impatient I actually am`;

const MIN_USEFUL_FACTS = 3;

/**
 * Copyable prompt the user can take to their own LLM (Claude, ChatGPT,
 * whichever they actually use) to generate anchor facts. The destination
 * LLM almost certainly knows them better than they can self-summarize in
 * the moment — this is the shortcut for users who freeze on the cold start.
 *
 * Format rules in the prompt are load-bearing: without them, the LLM
 * returns bulleted lists with headers that don't paste cleanly back.
 */
const LLM_PROMPT = `I'm building a digital brain — a worldview file that an AI will use to roleplay as me. It needs ANCHOR FACTS: short declarative statements about who I am that shape every response. Examples: "Jewish, secular but culturally identifying" / "Build drones — founder of a hardware startup" / "Mexican, grew up in CDMX, moved to the US at 22" / "The illness I had at 24 still shapes how I think about time."

Based on what you know about me, write 8–15 anchor facts that comprehensively capture who I am. Cover all of these where relevant:
- Heritage / where I'm from / language / culture
- Profession / what I actually do day-to-day / the work I'm known for
- Family / who depends on me / partner, kids, parents
- Formation / experiences that shape how I see the world / things I survived or built
- Things most people misunderstand about me
- Stable preferences / aesthetic / temperament that feel core

Format rules:
- One fact per line. No bullets, no numbering, no headers, no preamble.
- Each fact is one declarative sentence under 15 words.
- Skip anything aspirational — only what is TRUE, not what I want to be true.
- Be specific. "Founder" is weak; "Founder of a drone hardware startup" is right.

Output only the facts, nothing else.`;

export function Stage15AnchorFacts({
  initial,
  worldviewName,
  onContinue,
  onSkip,
  onBack,
}: Stage15Props) {
  const [raw, setRaw] = useState<string>(
    initial && initial.length > 0 ? initial.join('\n') : '',
  );
  const [copied, setCopied] = useState(false);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    const t = window.setTimeout(() => textareaRef.current?.focus(), 250);
    return () => window.clearTimeout(t);
  }, []);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(LLM_PROMPT);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      // clipboard unavailable — fall through silently
    }
  };

  const facts = raw
    .split('\n')
    .map((s) => s.trim())
    .filter((s) => s.length > 0);

  const factCount = facts.length;
  const canContinue = factCount > 0;
  const isThin = factCount > 0 && factCount < MIN_USEFUL_FACTS;

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    if (canContinue) onContinue(facts);
  };

  return (
    <div className="onboarding-anchor">
      <header className="onboarding-anchor__header">
        <button
          type="button"
          className="onboarding-anchor__back"
          onClick={onBack}
        >
          ← Back to questions
        </button>
        <p className="onboarding-anchor__eyebrow">— Almost there —</p>
        <h2 className="onboarding-anchor__title">
          A few facts that anchor <em>everything else</em>.
        </h2>
        <p className="onboarding-anchor__lede">
          {worldviewName} now knows <em>how</em> you reason. Tell it <em>who</em>{' '}
          you are. Heritage, profession, family, a formative experience,
          something most people get wrong about you. These ride in every
          conversation — without them the brain sounds generic on anything
          personal.
        </p>
      </header>

      <aside className="onboarding-anchor__helper" aria-label="Optional LLM shortcut">
        <div className="onboarding-anchor__helper-intro">
          <span className="onboarding-anchor__helper-eyebrow">— Optional shortcut —</span>
          <p className="onboarding-anchor__helper-copy">
            Don&rsquo;t want to write these from scratch? Take this prompt to
            the LLM you already use — it knows you better than you can
            summarize in a minute. Paste the result below.
          </p>
        </div>

        <div className="onboarding-anchor__prompt">
          <pre>{LLM_PROMPT}</pre>
          <button
            type="button"
            className="onboarding-anchor__copy"
            onClick={handleCopy}
          >
            {copied ? 'Copied' : 'Copy prompt'}
          </button>
        </div>
      </aside>

      <form className="onboarding-anchor__form" onSubmit={handleSubmit}>
        <label
          className="onboarding-anchor__label"
          htmlFor="onboarding-anchor-facts"
        >
          One fact per line. Three or more is the sweet spot.
        </label>
        <textarea
          ref={textareaRef}
          id="onboarding-anchor-facts"
          className="onboarding-anchor__textarea"
          value={raw}
          onChange={(e) => setRaw(e.target.value)}
          placeholder={PLACEHOLDER}
          rows={10}
          spellCheck
          maxLength={4000}
        />

        <div className="onboarding-anchor__meta">
          <span
            className={
              'onboarding-anchor__count' +
              (isThin ? ' onboarding-anchor__count--thin' : '') +
              (factCount >= MIN_USEFUL_FACTS
                ? ' onboarding-anchor__count--good'
                : '')
            }
          >
            {factCount === 0
              ? 'No facts yet'
              : factCount === 1
                ? '1 fact'
                : `${factCount} facts`}
            {isThin && ' — add a couple more for stronger grounding'}
          </span>
        </div>

        <div className="onboarding-anchor__actions">
          <button
            type="button"
            className="onboarding-anchor__skip"
            onClick={onSkip}
          >
            Skip — I&rsquo;ll add these later
          </button>
          <button
            type="submit"
            className="onboarding-anchor__continue"
            disabled={!canContinue}
          >
            Continue to the test <span aria-hidden="true">→</span>
          </button>
        </div>
      </form>
    </div>
  );
}
